import { parseOuiFile } from '@/features/oui/parseOuiFile'
import {
  applySkinPackageToProject,
  applySkinResources,
  parseSkinOuiPackage,
  type SkinProgressCallback,
} from '@/features/skin-loader/runtime'
import { parseVirtualResourcesJson, serializeVirtualResources } from '@/features/virtual-resource/persistence'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import MainProject from '@/model/MainProject'
import SystemData from '@/model/SystemData'
import DataFileMap from '@/model/Resource/DataFileMap'
import GameMallNewModel from '@/model/MallNewClass/GameMallNewModel'
import GameMallLimiteModel from '@/model/MallLimiteClass/GameMallLimiteModel'
import WelfareModel from '@/model/Welfare/WelfareModel'
import CGAppreciate from '@/model/GameSystemData/CGData/CGAppreciate'
import BGMAppreciate from '@/model/GameSystemData/BGMData/BGMAppreciate'
import SaveLoadData from '@/model/GameSystemData/SaveLoadData'
import TalkMessageData from '@/model/GameSystemData/MessageData/TalkMessageData'
import GameTitleData from '@/model/GameSystemData/GameTitleData'
import GameMenuData from '@/model/GameSystemData/GameMenuData'
import BtnList from '@/model/GameSystemData/BtnList'
import GameReplay from '@/model/GameSystemData/GameReplay'
import GameSettingData from '@/model/GameSystemData/GameSettingData'
import TotalSetting from '@/model/GameSystemData/TotalSetting'
import CustomUI from '@/model/CustomUI/CustomUI'
import FloatButton from '@/model/FloatButton/FloatButton'
import BubblePerson from '@/model/GameSystemData/BubblePerson'
import { FileReader, FileWriter } from '@/platform'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { buildCurrentProjectResourceUrl } from '@/features/static-resource/resourceCdn'

export type VirtualRoots = Record<ResourceCategoryKey, DataFileMap | null>
type ProjectResourceUrlResolver = (md5: string) => string

export type OuiqTemplateMeta = {
  id?: string
  name: string
  sourceOuiName: string
  width: number
  height: number
  dataVersion: number
  formatVersion: number
}

export type UiTemplateMeta = Omit<OuiqTemplateMeta, 'formatVersion'> & {
  formatVersion?: number
  sourceKind: 'oui' | 'ouiq'
}

export type ParsedUiTemplatePackage = {
  meta: UiTemplateMeta
  project: MainProject
  roots: VirtualRoots
  projectOuiPayload: Uint8Array
}

const OUIQ_MAGIC = 'OUIQTMPL'
const OUIQ_FORMAT_VERSION = 2

export type ParsedOuiqTemplate = {
  meta: OuiqTemplateMeta
  projectOuiPayload: Uint8Array
  virtualRoots: VirtualRoots
}

export function createEmptyVirtualRoots(): VirtualRoots {
  return {
    audio: null,
    graphic: null,
    font: null,
    video: null,
  }
}

export function cloneVirtualRoot(root: DataFileMap | null): DataFileMap | null {
  return root ? new DataFileMap(root.toJson()) : null
}

export function cloneVirtualRoots(
  roots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>,
): VirtualRoots {
  return {
    audio: cloneVirtualRoot(roots.audio ?? null),
    graphic: cloneVirtualRoot(roots.graphic ?? null),
    font: cloneVirtualRoot(roots.font ?? null),
    video: cloneVirtualRoot(roots.video ?? null),
  }
}

function writeBlock(writer: FileWriter, bytes: Uint8Array) {
  writer.writeInt(bytes.byteLength)
  writer.writeBytes(bytes)
}

function readBlock(reader: FileReader, label: string) {
  const length = reader.readInt()
  if (length < 0 || reader.getRemainingBytes() < length) {
    throw new Error(`${label} 长度非法`)
  }
  return reader.readSome(length)
}

function buildLightweightProjectPayload(project: MainProject, dataVersion: number): Uint8Array {
  const writer = new FileWriter()
  writer.writeInt(dataVersion)
  writer.writeString(project.UiName)
  writer.writeInt(project.Bitmap.length)
  writer.writeBytes(project.Bitmap)
  writer.writeInt(project.Width)
  writer.writeInt(project.Height)

  const systemWriter = new FileWriter()
  project.systemData.CG.writeBin(systemWriter)
  project.systemData.BGM.writeBin(systemWriter)
  project.systemData.Sldata.writeBin(systemWriter)
  project.systemData.Talk.writeBin(systemWriter)
  project.systemData.Title.writeBin(systemWriter)
  project.systemData.GameMenu.writeBin(systemWriter)
  project.systemData.btnList.writeBin(systemWriter)
  project.systemData.backPlay.writeBin(systemWriter)
  systemWriter.writeBool(project.systemData.IsShowSystemMenu)
  project.systemData.SystemMenu.writeBin(systemWriter)
  project.systemData.totalSetting.writeBin(systemWriter)
  new CustomUI().writeBin(systemWriter)
  project.systemData.floatButton.writeBin(systemWriter)
  project.systemData.bubblePersion.writeBin(systemWriter)
  writeBlock(writer, systemWriter.toUint8Array())

  if (project.GameMallNew) {
    const mallWriter = new FileWriter()
    project.GameMallNew.writeVersionedOui(mallWriter, false)
    writeBlock(writer, mallWriter.toUint8Array())
  } else {
    writeBlock(writer, new Uint8Array(0))
  }

  const mallLimiteWriter = new FileWriter()
  ;(project.GameMallLimite ?? new GameMallLimiteModel(
    { Width: project.Width, Height: project.Height },
    project.systemData.totalSetting.FontColor_UI,
  )).writeVersionedOui(mallLimiteWriter, false)
  writeBlock(writer, mallLimiteWriter.toUint8Array())

  const welfareWriter = new FileWriter()
  ;(project.Welfare ?? new WelfareModel({ Width: project.Width, Height: project.Height })).writeVersionedOui(welfareWriter)
  writeBlock(writer, welfareWriter.toUint8Array())
  return writer.toUint8Array()
}

function parseLightweightProjectPayload(source: Uint8Array): MainProject {
  const reader = new FileReader(source)
  const dataVersion = Math.max(1, Number(reader.readInt() || 0))
  const project = new MainProject()
  project.dataVer = dataVersion
  project.projectConstant.dataVersion = dataVersion
  project.UiName = reader.readString()
  project.Bitmap = reader.readSome(reader.readInt())
  project.Width = reader.readInt()
  project.Height = reader.readInt()

  const systemReader = new FileReader(readBlock(reader, 'systemData'))
  const systemData = new SystemData()
  systemData.CG = new CGAppreciate()
  systemData.CG.readBin(systemReader)
  systemData.BGM = new BGMAppreciate()
  systemData.BGM.readBin(systemReader)
  systemData.Sldata = new SaveLoadData()
  systemData.Sldata.readBin(systemReader)
  systemData.Talk = new TalkMessageData()
  systemData.Talk.readBin(systemReader)
  systemData.Title = new GameTitleData()
  systemData.Title.readBin(systemReader)
  systemData.GameMenu = new GameMenuData()
  systemData.GameMenu.readBin(systemReader)
  systemData.btnList = new BtnList()
  systemData.btnList.readBin(systemReader)
  systemData.backPlay = new GameReplay()
  systemData.backPlay.readBin(systemReader)
  systemData.IsShowSystemMenu = systemReader.readBool()
  systemData.SystemMenu = new GameSettingData()
  systemData.SystemMenu.readBin(systemReader)
  systemData.totalSetting = new TotalSetting()
  systemData.totalSetting.readBin(systemReader)
  systemData.customUI = new CustomUI()
  systemData.customUI.readBinSync(systemReader)
  systemData.floatButton = new FloatButton()
  systemData.floatButton.readBin(systemReader)
  systemData.bubblePersion = new BubblePerson()
  systemData.bubblePersion.readBin(systemReader)
  project.systemData = systemData

  const mallBytes = readBlock(reader, 'gameMallNew')
  if (mallBytes.length) {
    const mall = new GameMallNewModel(project.systemData.totalSetting.FontColor_UI, {
      Width: project.Width,
      Height: project.Height,
    })
    mall.readVersionedOui(new FileReader(mallBytes), dataVersion, false)
    project.GameMallNew = mall
  } else {
    project.GameMallNew = null
  }

  const mallLimite = new GameMallLimiteModel(
    { Width: project.Width, Height: project.Height },
    project.systemData.totalSetting.FontColor_UI,
  )
  mallLimite.readVersionedOui(new FileReader(readBlock(reader, 'gameMallLimite')), false)
  mallLimite.LimitTime = false
  project.GameMallLimite = mallLimite

  const welfare = new WelfareModel({ Width: project.Width, Height: project.Height })
  welfare.readVersionedOui(new FileReader(readBlock(reader, 'welfare')))
  project.Welfare = welfare
  project.FilesBuf = []
  return project
}

function resolveOuiqProjectResourceUrl(md5: string, resolver?: ProjectResourceUrlResolver) {
  return resolver?.(md5) || buildCurrentProjectResourceUrl(md5)
}

function normalizeOuiqMeta(metaRaw: Partial<OuiqTemplateMeta>, formatVersion: number): OuiqTemplateMeta {
  return {
    id: metaRaw.id?.trim() || '',
    name: String(metaRaw.name || '').trim() || '未命名模板',
    sourceOuiName: String(metaRaw.sourceOuiName || '').trim() || 'unknown.oui',
    width: Math.max(1, Number(metaRaw.width || 0)),
    height: Math.max(1, Number(metaRaw.height || 0)),
    dataVersion: Math.max(1, Number(metaRaw.dataVersion || 0)),
    formatVersion,
  }
}

function toParsedUiTemplatePackage(template: ParsedOuiqTemplate): ParsedUiTemplatePackage {
  const project = template.meta.formatVersion >= 2
    ? parseLightweightProjectPayload(template.projectOuiPayload)
    : (() => {
        const legacyProject = new MainProject()
        parseOuiFile(template.projectOuiPayload, template.meta.dataVersion, legacyProject, true)
        return legacyProject
      })()
  return {
    meta: {
      ...template.meta,
      sourceKind: 'ouiq',
    },
    project,
    roots: cloneVirtualRoots(template.virtualRoots),
    projectOuiPayload: template.projectOuiPayload,
  }
}

export function buildOuiqTemplate(params: {
  meta: Omit<OuiqTemplateMeta, 'formatVersion'>
  projectOuiPayload: Uint8Array
  virtualRoots: Partial<Record<ResourceCategoryKey, DataFileMap | null>>
}): Uint8Array {
  const writer = new FileWriter()
  writer.writeStringRaw(OUIQ_MAGIC)
  writer.writeInt(OUIQ_FORMAT_VERSION)

  const metaBytes = new TextEncoder().encode(JSON.stringify({
    ...params.meta,
    formatVersion: OUIQ_FORMAT_VERSION,
  }))
  writeBlock(writer, metaBytes)
  writeBlock(writer, params.projectOuiPayload)
  writeBlock(writer, new TextEncoder().encode(serializeVirtualResources(params.virtualRoots)))
  return writer.toUint8Array()
}

export function parseOuiqTemplate(source: ArrayBuffer | Uint8Array): ParsedOuiqTemplate {
  const reader = new FileReader(source instanceof Uint8Array ? source : new Uint8Array(source))
  const magic = reader.readStringLen(OUIQ_MAGIC.length)
  if (magic !== OUIQ_MAGIC) {
    throw new Error('不是有效的 ouiq 模板文件')
  }

  const formatVersion = reader.readInt()
  if (formatVersion !== 1 && formatVersion !== OUIQ_FORMAT_VERSION) {
    throw new Error(`不支持的 ouiq 版本：${formatVersion}`)
  }

  const metaBytes = readBlock(reader, 'meta')
  const metaRaw = JSON.parse(new TextDecoder().decode(metaBytes)) as Partial<OuiqTemplateMeta>
  const projectOuiPayload = readBlock(reader, 'projectOuiPayload')
  const rootsBytes = readBlock(reader, 'virtualRoots')
  const virtualRoots = cloneVirtualRoots(parseVirtualResourcesJson(new TextDecoder().decode(rootsBytes)))

  if (!reader.isReadEnd()) {
    throw new Error('ouiq 模板存在未识别的尾部数据')
  }

  return {
    meta: normalizeOuiqMeta(metaRaw, formatVersion),
    projectOuiPayload,
    virtualRoots,
  }
}

export function parseUiTemplatePackageFromOuiqSource(
  source: ParsedOuiqTemplate | ArrayBuffer | Uint8Array,
): ParsedUiTemplatePackage {
  const template = source instanceof Uint8Array || source instanceof ArrayBuffer
    ? parseOuiqTemplate(source)
    : source
  return toParsedUiTemplatePackage(template)
}

/**
 * 将本地 `.oui/.oui2` 解析为统一模板包，并按指定资源 URL 生成器写入虚拟资源地址。
 */
export async function parseUiTemplatePackageFromOuiSource(params: {
  source: ArrayBuffer | Uint8Array
  templateId?: string
  templateName: string
  sourceOuiName: string
  dataVersion: number
  resolveProjectResourceUrl?: ProjectResourceUrlResolver
  onProgress?: SkinProgressCallback
}): Promise<ParsedUiTemplatePackage> {
  params.onProgress?.(5, '解析 oui 模板…')
  const project = new MainProject()
  const skinPackage = parseSkinOuiPackage({
    project,
    source: params.source,
    dataVersion: params.dataVersion,
    isNew: true,
    onProgress: params.onProgress,
  })

  applySkinPackageToProject(project, skinPackage)
  const dataVersion = Math.max(
    1,
    Number(params.dataVersion || 0),
    Number(project.projectConstant.dataVersion || 0),
    Number(project.dataVer || 0),
  )
  project.projectConstant.dataVersion = dataVersion
  project.dataVer = dataVersion

  params.onProgress?.(52, '生成模板虚拟资源…')
  const applied = await applySkinResources({
    project,
    roots: createEmptyVirtualRoots(),
    entries: skinPackage.entries,
    rewriteProjectPaths: false,
    onProgress: (progress, status) => {
      params.onProgress?.(52 + Math.round(progress * 0.38), status)
    },
    resolveResource: async (entry) => {
      const bytes = new Uint8Array(await BrowserFileUtils.blobToArrayBuffer(entry.file))
      const md5 = BrowserFileUtils.getMd5HashFromUint8Array(bytes)
      return {
        resourceUrl: resolveOuiqProjectResourceUrl(md5, params.resolveProjectResourceUrl),
        fileName: entry.fileName,
      }
    },
  })
  const projectOuiPayload = buildLightweightProjectPayload(project, dataVersion)

  return {
    meta: {
      id: params.templateId?.trim() || '',
      name: params.templateName,
      sourceOuiName: params.sourceOuiName,
      width: project.Width || skinPackage.width,
      height: project.Height || skinPackage.height,
      dataVersion,
      sourceKind: 'oui',
    },
    project,
    roots: cloneVirtualRoots(applied.roots),
    projectOuiPayload,
  }
}

export function parseOuiqProject(source: ParsedOuiqTemplate | ArrayBuffer | Uint8Array): MainProject {
  return parseUiTemplatePackageFromOuiqSource(source).project
}

/**
 * 从本地 `.oui/.oui2` 构建可内置分发的 `.ouiq` 模板文件。
 */
export async function buildOuiqTemplateFromOui(params: {
  source: ArrayBuffer | Uint8Array
  templateId?: string
  templateName: string
  sourceOuiName: string
  dataVersion: number
  resolveProjectResourceUrl?: ProjectResourceUrlResolver
  onProgress?: SkinProgressCallback
}): Promise<Uint8Array> {
  const templatePackage = await parseUiTemplatePackageFromOuiSource(params)
  params.onProgress?.(92, '写出 ouiq 模板…')
  const templateBytes = buildOuiqTemplate({
    meta: {
      id: templatePackage.meta.id,
      name: templatePackage.meta.name,
      sourceOuiName: templatePackage.meta.sourceOuiName,
      width: templatePackage.meta.width,
      height: templatePackage.meta.height,
      dataVersion: templatePackage.meta.dataVersion,
    },
    projectOuiPayload: templatePackage.projectOuiPayload,
    virtualRoots: templatePackage.roots,
  })
  verifyOuiqTemplateRoundtrip(templateBytes)
  params.onProgress?.(100, 'ouiq 模板生成完成')
  return templateBytes
}

export function verifyOuiqTemplateRoundtrip(source: ArrayBuffer | Uint8Array): ParsedOuiqTemplate {
  const template = parseOuiqTemplate(source)
  parseUiTemplatePackageFromOuiqSource(template)
  return template
}
